import { useState } from 'react';
import { ArrowTriangleIcon, BackspaceIcon, ShiftIcon } from '@/icons';
import styles from '@/components/VirtualKeyboard/VirtualKeyboard.module.css';

const NUM_ROW = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
const ROW_1 = ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'];
const ROW_2 = ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'];
const ROW_3 = ['z', 'x', 'c', 'v', 'b', 'n', 'm'];
const SYMBOLS = ['.', '(', ')', ',', '/', '+', '-', '#', '=', '[', ']', '<', '>'];

/** Caret/history triangle sized/tinted for the keyboard; `rotate` orients it. */
function TriangleIcon({ rotate }: { rotate: number }): JSX.Element {
  return (
    <ArrowTriangleIcon
      width={39}
      height={28}
      aria-hidden="true"
      style={{ transform: `rotate(${rotate}deg)`, color: '#9292bb' }}
    />
  );
}

export interface VirtualKeyboardProps {
  /** A character was pressed — insert it into the target field. */
  readonly onInput: (text: string) => void;
  /** Backspace pressed — delete the character before the caret. */
  readonly onBackspace: () => void;
  /** "Set" key pressed — submit the staged lines to the machine. */
  readonly onEnter?: () => void;
  /** Disable the "Set" key (nothing staged, or UI locked). */
  readonly submitDisabled?: boolean;
  /** Arrow ←/→ pressed — move the text caret. */
  readonly onCaretMove?: (dir: 'left' | 'right') => void;
  /** Arrow ↑/↓ pressed — navigate the command history. */
  readonly onHistoryNav?: (dir: 'up' | 'down') => void;
  readonly className?: string;
}

export function VirtualKeyboard({
  onInput,
  onBackspace,
  onEnter,
  submitDisabled = false,
  onCaretMove,
  onHistoryNav,
  className,
}: VirtualKeyboardProps): JSX.Element {
  const [shift, setShift] = useState(false);
  const [symOpen, setSymOpen] = useState(false);

  const preventBlur = (e: React.PointerEvent) => {
    e.preventDefault();
  };

  const typeChar = (char: string) => {
    onInput(shift ? char.toUpperCase() : char);
    if (shift) setShift(false);
  };

  const renderKeys = (keys: readonly string[], variant?: 'num') =>
    keys.map((key) => (
      <button
        key={key}
        type="button"
        className={`${styles.key} ${variant === 'num' ? styles.keyNum : ''}`}
        onPointerDown={preventBlur}
        onClick={() => typeChar(key)}
      >
        {shift ? key.toUpperCase() : key}
      </button>
    ));

  const sheetClass = [styles.sheet, className ?? ''].filter(Boolean).join(' ');

  return (
    <div className={sheetClass} role="group" aria-label="On-screen keyboard">
      {/* SYM symbol popover */}
      {symOpen && (
        <div className={styles.symPopover} role="group" aria-label="Symbols">
          {SYMBOLS.map((sym) => (
            <button
              key={sym}
              type="button"
              className={`${styles.key} ${styles.symKey}`}
              onPointerDown={preventBlur}
              onClick={() => onInput(sym)}
            >
              {sym}
            </button>
          ))}
        </div>
      )}

      {/* Row 1: numbers + backspace */}
      <div className={styles.row}>
        {renderKeys(NUM_ROW, 'num')}
        <button
          type="button"
          className={`${styles.key} ${styles.keyFn} ${styles.keyAlt} ${styles.keyBackspace}`}
          onPointerDown={preventBlur}
          onClick={onBackspace}
          aria-label="Backspace"
        >
          <BackspaceIcon width={42} height={42} aria-hidden="true" style={{ color: '#ffffff' }} />
        </button>
      </div>

      {/* Row 2 */}
      <div className={styles.row}>{renderKeys(ROW_1)}</div>

      {/* Row 3: letters + Set */}
      <div className={styles.row}>
        {renderKeys(ROW_2)}
        <button
          type="button"
          className={`${styles.key} ${styles.keySet}`}
          onPointerDown={preventBlur}
          onClick={onEnter}
          disabled={submitDisabled}
        >
          Set
        </button>
      </div>

      {/* Row 4: shift + letters + up arrow */}
      <div className={styles.row}>
        <button
          type="button"
          className={`${styles.key} ${styles.keyFn} ${styles.keyAlt} ${styles.keyShift} ${shift ? styles.keyFnActive : ''}`}
          onPointerDown={preventBlur}
          onClick={() => setShift((s) => !s)}
          aria-label="Shift"
          aria-pressed={shift}
        >
          <ShiftIcon width={34} height={34} aria-hidden="true" style={{ color: '#ffffff' }} />
        </button>
        {renderKeys(ROW_3)}
        <button
          type="button"
          className={`${styles.key} ${styles.keyArrow} ${styles.keyArrowUp}`}
          onPointerDown={preventBlur}
          onClick={() => onHistoryNav?.('up')}
          aria-label="Previous command"
        >
          <TriangleIcon rotate={0} />
        </button>
      </div>

      {/* Row 5: SYM + space + caret/history arrows */}
      <div className={styles.row}>
        <button
          type="button"
          className={`${styles.key} ${styles.keyFn} ${styles.keyAlt} ${styles.keySym} ${symOpen ? styles.keyFnActive : ''}`}
          onPointerDown={preventBlur}
          onClick={() => setSymOpen((s) => !s)}
          aria-label="Symbols"
          aria-pressed={symOpen}
        >
          SYM
        </button>
        <button
          type="button"
          className={`${styles.key} ${styles.keySpace} ${styles.keyAlt}`}
          onPointerDown={preventBlur}
          onClick={() => onInput(' ')}
        >
          space
        </button>
        <button
          type="button"
          className={`${styles.key} ${styles.keyArrow}`}
          onPointerDown={preventBlur}
          onClick={() => onCaretMove?.('left')}
          aria-label="Caret left"
        >
          <TriangleIcon rotate={270} />
        </button>
        <button
          type="button"
          className={`${styles.key} ${styles.keyArrow}`}
          onPointerDown={preventBlur}
          onClick={() => onHistoryNav?.('down')}
          aria-label="Next command"
        >
          <TriangleIcon rotate={180} />
        </button>
        <button
          type="button"
          className={`${styles.key} ${styles.keyArrow}`}
          onPointerDown={preventBlur}
          onClick={() => onCaretMove?.('right')}
          aria-label="Caret right"
        >
          <TriangleIcon rotate={90} />
        </button>
      </div>
    </div>
  );
}
